import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Session, User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { api, setUnauthorizedHandler } from "@/lib/api";
import { getSupabase, isSupabaseConfigured } from "@/lib/supabase";
import {
  getAppleIdTokenNative,
  getAuthPlatform,
  getGoogleIdTokenNative,
} from "./nativeAuth";
import { authCallbackUrl } from "./deepLinks";
import { claimPersistedState, resetUserState } from "./userScope";

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  configured: boolean;
  /** Sign out and wipe every piece of user-scoped local state. */
  signOut: () => Promise<void>;
  /** Permanently delete the account (DELETE /api/v1/me), then sign out. */
  deleteAccount: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const configured = isSupabaseConfigured();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(configured);

  useEffect(() => {
    if (!configured) return;
    const supabase = getSupabase();
    let active = true;

    void supabase.auth
      .getSession()
      .then(async ({ data }) => {
        // Scope persisted stores to this user BEFORE any authed page can
        // render (RequireAuth waits on isLoading): otherwise MenuPage could
        // auto-validate a previous user's draft under this account (M28).
        if (data.session?.user) {
          await claimPersistedState(data.session.user.id, queryClient);
        }
        if (!active) return;
        setSession(data.session);
        setIsLoading(false);
      })
      .catch(() => {
        if (active) setIsLoading(false);
      });

    // NB: never await Supabase calls inside this callback (supabase-js
    // holds its auth lock while invoking it).
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next);
      if (event === "SIGNED_OUT") {
        void resetUserState(queryClient);
      } else if (event === "SIGNED_IN" && next?.user) {
        void claimPersistedState(next.user.id, queryClient);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [configured, queryClient]);

  const signOut = useCallback(async () => {
    if (!configured) return;
    const auth = getSupabase().auth;
    try {
      const { error } = await auth.signOut();
      // Server-side revoke failed (offline, session already gone): still
      // drop the local session so the device is signed out.
      if (error) await auth.signOut({ scope: "local" });
    } catch {
      await auth.signOut({ scope: "local" }).catch(() => undefined);
    } finally {
      setSession(null);
      await resetUserState(queryClient);
    }
  }, [configured, queryClient]);

  // A 401 on an authenticated API call means the token is no longer
  // accepted (revoked / user deleted): sign out locally → RequireAuth
  // sends the user to /login.
  useEffect(() => {
    if (!configured) return;
    return setUnauthorizedHandler(() => {
      void getSupabase()
        .auth.signOut({ scope: "local" })
        .catch(() => undefined)
        .finally(() => {
          setSession(null);
          void resetUserState(queryClient);
        });
    });
  }, [configured, queryClient]);

  const deleteAccount = useCallback(async () => {
    if (!configured) return;
    await api("/api/v1/me", { method: "DELETE" });
    // The auth user no longer exists: a global sign-out would 4xx, so only
    // drop the local session.
    await getSupabase()
      .auth.signOut({ scope: "local" })
      .catch(() => undefined);
    setSession(null);
    await resetUserState(queryClient);
  }, [configured, queryClient]);

  const signInWithApple = useCallback(async () => {
    if (!configured) return;

    if (getAuthPlatform() === "web") {
      await getSupabase().auth.signInWithOAuth({
        provider: "apple",
        options: { redirectTo: authCallbackUrl(window.location.origin) },
      });
      return;
    }

    try {
      const { idToken, nonce } = await getAppleIdTokenNative();
      const { error } = await getSupabase().auth.signInWithIdToken({
        provider: "apple",
        token: idToken,
        nonce,
      });
      if (error) throw error;
      // Navigation + onboarding sync happen in LoginPage once the new
      // session propagates (shared runAfterAuth).
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Apple sign-in failed");
      throw err;
    }
  }, [configured]);

  const signInWithGoogle = useCallback(async () => {
    if (!configured) return;

    if (getAuthPlatform() === "web") {
      await getSupabase().auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: authCallbackUrl(window.location.origin) },
      });
      return;
    }

    try {
      const idToken = await getGoogleIdTokenNative();
      const { error } = await getSupabase().auth.signInWithIdToken({
        provider: "google",
        token: idToken,
      });
      if (error) throw error;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Google sign-in failed");
      throw err;
    }
  }, [configured]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      isLoading,
      configured,
      signOut,
      deleteAccount,
      signInWithApple,
      signInWithGoogle,
    }),
    [
      session,
      isLoading,
      configured,
      signOut,
      deleteAccount,
      signInWithApple,
      signInWithGoogle,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
